import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import ErrorModal from "../../../utils/ErrorModal";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

const Step2 = ({ tender, data, setData, setStep, user, bid }) => {
  const fileInputRef = useRef(null);
  const [errorModal, setErrorModal] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const navigate = useNavigate();
  const { tenderId } = useParams();
  const [isImporting, setIsImporting] = useState(false);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleChange = (index, field, value) => {
    if (value.includes(".")) {
      const [intPart, decPart] = value.split(".");
      if (decPart.length > 2) {
        value = intPart + "." + decPart.slice(0, 2);
      }
    }
    const updatedData = [...data];
    updatedData[index][field] = value;
    updatedData[index].total =
      updatedData[index].quantity * updatedData[index].rate;

    setData(updatedData);
  };

  const exportToExcel = () => {
    const cleanedData = data.map((row) => ({
      itemName: row.itemName || "",
      description: row.description || "",
      section: row.section || "",
      unit: row.unit || "",
      quantity: row.quantity || 0,
      rate: row.rate || 0,
      total: row.total || 0,
    }));
    const worksheet = XLSX.utils.json_to_sheet(cleanedData, {
      cellStyles: true,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Work Items");
    XLSX.writeFile(workbook, "BidDetails.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const bufferArray = e.target.result;
      const workbook = XLSX.read(bufferArray, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet);

      const normalize = (val) =>
        (val || "").toString().replace(/\s+/g, " ").trim().toLowerCase();

      const updatedData = data.map((row) => {
        const matchingRow = parsedData.find(
          (excelRow) =>
            excelRow.itemName === row.itemName &&
            normalize(excelRow.description) === normalize(row.description) &&
            excelRow.section === row.section &&
            excelRow.unit === row.unit &&
            excelRow.quantity === row.quantity,
        );
        let rate = parseFloat(matchingRow?.rate);
        if (isNaN(rate)) rate = 0;
        return {
          ...row,
          rate: rate.toFixed(2),
          total: parseFloat(
            (row.quantity || 0) * (rate.toFixed(2) || 0),
          ).toFixed(2),
        };
      });

      setData(updatedData);
      setIsImporting(false);
      event.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const grandTotal = data
    ?.reduce((sum, row) => sum + parseFloat(row.quantity * row.rate || 0), 0)
    .toFixed(2);

  const handleNext = async () => {
    const filteredData = data.filter((item) => {
      const isAllEmpty =
        (!item.itemName || item.itemName.toString().trim() === "") &&
        (!item.description || item.description.toString().trim() === "") &&
        (!item.section || item.section.toString().trim() === "") &&
        (!item.unit || item.unit.toString().trim() === "") &&
        (!item.quantity || Number(item.quantity) <= 0) &&
        (!item.rate || Number(item.rate) <= 0);

      return !isAllEmpty; // drop only empty rows
    });

    const hasInvalidRate = filteredData.some(
      (item) => !item.rate || Number(item.rate) <= 0 || item.rate === "",
    );
    if (filteredData.length === 0 || hasInvalidRate) {
      setErrorModal(true);
      return;
    }
    setBtnLoading(true);
    try {
      if (tender.isSingleBid && bid === undefined) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/draft-bid`,
          {
            vendorId: vendorId,
            tenderId: tender.tenderId,
            // ts1: bid.ts1,
            // ts2: bid.ts2,
            // ts3: bid.ts3,
            // ts4: bid.ts4,
            // ts5: bid.ts5,
            // ts6: bid.ts6,
            // ts7: bid.ts7,
            // ts8: bid.ts8,
            // ts1Filename: fileNames.ts1,
            // ts2Filename: fileNames.ts2,
            // ts3Filename: fileNames.ts3,
            // ts4Filename: fileNames.ts4,
            // ts5Filename: fileNames.ts5,
            // ts6Filename: fileNames.ts6,
            // ts7Filename: fileNames.ts7,
            // ts8Filename: fileNames.ts8,
          },
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
      }
      const indexedData = filteredData.map((item, index) => ({
        ...item,
        itemOrder: index,
      }));
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/draft-bid`,
        {
          vendorId: vendorId,
          tenderId: tender.tenderId,
          draftBidWorkItems: indexedData,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setStep((prev) => prev + 1);
      setData(filteredData);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    if (tender.isSingleBid) {
      navigate("/vendor/dashboard");
    } else {
      navigate(`/vendor/dashboard/submit-bid/draft/${tenderId}`);
      setStep((prev) => prev - 1);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {isImporting && (
        <div className="loading-div uploading-div">
          Uploading Sheet... <CircularProgress color="#fff" />
        </div>
      )}
      {btnLoading && <div className="loading-div"></div>}
      <h2>Work Items Details</h2>
      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            minWidth: "100%",
            marginTop: "10px",
            textAlign: "center",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Item Name</th>
              <th>Description</th>
              <th>Section</th>
              <th>Unit</th>
              <th>Quantity</th>
              <th style={{ width: "150px" }}>Rate (in &#8377;)</th>
              <th>Total (in &#8377;)</th>
            </tr>
          </thead>
          <tbody>
            {data
              .sort((a, b) => a.itemOrder - b.itemOrder)
              .map((row, index) => (
                <tr key={index}>
                  <td>
                    <p>{row.itemName}</p>
                  </td>
                  <td>
                    <p>{row.description}</p>
                  </td>
                  <td>
                    <p>{row.section}</p>
                  </td>
                  <td>
                    <p>{row.unit}</p>
                  </td>
                  <td>
                    <p>{row.quantity}</p>
                  </td>
                  <td>
                    <input
                      className="table-input"
                      type="number"
                      value={row.rate}
                      onChange={(e) =>
                        handleChange(index, "rate", e.target.value)
                      }
                      step={0.01}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <p>
                      {(
                        parseFloat(row.rate || 0) *
                        parseFloat(row.quantity || 0)
                      ).toFixed(2)}
                    </p>
                  </td>
                </tr>
              ))}

            <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
              <td colSpan={6} style={{ textAlign: "right", padding: "20px" }}>
                Grand Total (in &#8377;):
              </td>
              <td style={{ width: "150px", textAlign: "center" }}>
                {grandTotal}
              </td>
            </tr>
          </tbody>
        </table>
        <Typography variant="p" fontWeight={700} fontSize={"14px"}>
          [Important Note: <br />
          - Uploading Sheet will overwrite existing work items. <br />-
          Modifying any data other than rate column may cause errors while
          uploading. ]
        </Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "end",
            gap: 50,
          }}
        >
          <Button className="btn-outlined" onClick={handleFileSelect}>
            Upload Excel
          </Button>
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              marginTop: "-50px",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>
      </div>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={importFromExcel}
        style={{ display: "none" }}
      />
      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={handleNext}>
          Next
        </Button>
      </Box>

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={"Item rates cannot be 0"}
      />
    </div>
  );
};

export default Step2;
